import type {
  RenderPrompts,
  RenderControl,
  RenderModelFamily,
  RenderEffort,
  RenderMode,
} from '@/types/render';
import {
  type EnvironmentSetting,
  EnvironmentSettingEnum,
  generateSeed,
} from '@/shared/constants';

// ===== RenderFormData type =====
export interface RenderFormData {
  version: number;
  // Note: project_goals and construction_region are project-level fields
  // auto-saved by the Project goals panel. The form does not carry them and
  // does not ship them with the render payload — the server snapshots them
  // from Project at render-create time. See ConceptRender::create on the API.
  style_ids: number[] | null;
  material_ids: number[] | null;
  colors: string[] | null;
  prompts: RenderPrompts;
  controls: RenderControl[] | null;
  output_style_id: number | null;
  environment_setting: EnvironmentSetting;
  seed: number;
  is_derivative?: boolean;
  render_model_family?: RenderModelFamily;
  render_effort?: RenderEffort;
  // Project references by category
  style_project_reference_ids: string[] | null;
  material_project_reference_ids: string[] | null;
  color_project_reference_ids: string[] | null;
  output_project_reference_ids: string[] | null;
  // Auto-use project references flags (when in Auto mode).
  // OUTPUT category has no auto flag — it's never auto-injected into the
  // first-stage prompt; only explicit output_project_reference_ids feed it.
  use_auto_style_refs: boolean;
  use_auto_material_refs: boolean;
  use_auto_color_refs: boolean;
  /**
   * Refine intent for the next render. NOT persisted — ephemeral Explore:
   * entering a context derives Refine whenever a render exists; the
   * in-session choice survives same-context re-fires of
   * `initializeFormData` and is reset only by a real context switch.
   */
  mode: RenderMode;
  parent_render_id: string | null;
  /**
   * Hi-Res rendering toggle. When true, the provider is asked for ~2K native
   * output (OpenAI quality=high / Gemini 2K). Pro+ plan feature, gated by
   * `is_feature_hires_render` on the active plan. Server rejects a
   * bypassed payload with `PACK_REQUIRED`.
   * Distinct from the post-render 4K Replicate upscale (`is_feature_upscale`).
   */
  is_hires: boolean;
  /**
   * Extended thinking toggle. When true, the design-prompt LLM uses a
   * stronger reasoning model. Pro+ plan feature, gated by
   * `is_feature_extended_thinking` on the active plan. Server rejects a
   * bypassed payload with `PACK_REQUIRED`.
   */
  is_extended_thinking: boolean;
}

// ===== Initial state =====
export const initialSeed = generateSeed();

export const initialFormData: RenderFormData = {
  version: 1,
  style_ids: null,
  material_ids: null,
  colors: null,
  prompts: {
    prompt_concept: '',
    prompt_angle: '',
  },
  controls: null,
  output_style_id: null,
  environment_setting: EnvironmentSettingEnum.NO_CHANGE,
  seed: initialSeed,
  is_derivative: false,
  render_model_family: 'AURORA',
  // New drafts default to Brainstorm — the cheapest tier (~4 tokens). It
  // stretches the free signup tokens across more generations so users feel the
  // product before the paywall, and matches the "≈N designs" count token packs
  // advertise (computed at the Brainstorm rate server-side).
  render_effort: 'BRAINSTORM',
  style_project_reference_ids: null,
  material_project_reference_ids: null,
  color_project_reference_ids: null,
  output_project_reference_ids: null,
  // Reference source defaults to "All project references": a fresh draft auto-
  // applies the project's uploaded references for style/material/color unless
  // the user explicitly switches to "No references" or "Selective references"
  // in the sub-panel. Mirrors Apple `RenderDraftState` and Android
  // `RenderFormState` defaults. Refine is the exception — it opts out (see
  // `getRefineBaseline`).
  use_auto_style_refs: true,
  use_auto_material_refs: true,
  use_auto_color_refs: true,
  mode: 'explore',
  parent_render_id: null,
  is_hires: false,
  is_extended_thinking: false,
};

export interface RenderUIState {
  selectedRenderId: string | null;
  showRenderDetails: boolean;
}

export const initialUIState: RenderUIState = {
  selectedRenderId: null,
  showRenderDetails: false,
};

// ===== Shared technical settings group =====

/**
 * The TECHNICAL settings group — engine + output axes (model family, effort,
 * extended thinking, output style, Hi-Res). Unlike the design fields, this
 * group is a SINGLE app-wide value per logged-in user: one value shared
 * across every concept/angle/render AND both Explore/Refine modes, persisted
 * under the user scope alone with NO concept/angle/render or mode suffix (see
 * `saveTechSettings` in render-settings-storage.ts). Flipping Hi-Res anywhere
 * shows everywhere.
 */
export const SHARED_TECH_SETTINGS_KEYS = [
  'render_model_family',
  'render_effort',
  'is_extended_thinking',
  // The render style (output style) is ONE choice with two mutually
  // exclusive representations: a preset (`output_style_id`) OR custom
  // file-based references (`output_project_reference_ids`). Both belong to
  // the shared technical group so the WHOLE choice carries across
  // Explore↔Refine and sticks app-wide — otherwise a custom file-based
  // render style is dropped on Refine and the picker falls back to "Photo".
  'output_style_id',
  'output_project_reference_ids',
  'is_hires',
] as const;

export type TechSettingsData = Pick<
  RenderFormData,
  (typeof SHARED_TECH_SETTINGS_KEYS)[number]
>;

export function extractTechSettings(
  form: Pick<RenderFormData, (typeof SHARED_TECH_SETTINGS_KEYS)[number]>
): TechSettingsData {
  return {
    render_model_family: form.render_model_family,
    render_effort: form.render_effort,
    is_extended_thinking: form.is_extended_thinking,
    output_style_id: form.output_style_id,
    output_project_reference_ids: form.output_project_reference_ids,
    is_hires: form.is_hires,
  };
}

// ===== Per-tab baselines (RENDER FORM SPEC rule 1) =====

/**
 * Baseline for the Explore tab (used as the no-changes anchor for diffs and
 * resets). When the source render is Refine, the Explore tab starts blank
 * (RENDER FORM SPEC rule 1) — `defaultValues` would still hold the parent's
 * refine settings, so consuming it directly would mark every Explore field
 * as "changed" against refine values and bloat the persisted draft / mark
 * every section as dirty in the UI.
 *
 * `seed` and `is_derivative` are carried over from `defaultValues` because
 * they are technical (not user intent) and consistent across both tabs.
 */
export function getExploreBaseline(
  defaults: Partial<RenderFormData> | null,
  parentMode: RenderMode
): Partial<RenderFormData> {
  if (parentMode === 'refine') {
    return {
      ...initialFormData,
      seed: defaults?.seed ?? initialFormData.seed,
      is_derivative: defaults?.is_derivative ?? initialFormData.is_derivative,
      // Hi-Res is technical (not user intent), carried over from defaults
      // alongside seed/is_derivative.
      is_hires: defaults?.is_hires ?? initialFormData.is_hires,
      // Extended thinking is technical (not user intent), carried over from
      // defaults alongside seed/is_derivative/is_hires.
      is_extended_thinking:
        defaults?.is_extended_thinking ?? initialFormData.is_extended_thinking,
    };
  }
  return defaults ?? initialFormData;
}

/**
 * Baseline for the Refine tab. Refine starts from a blank DESIGN form by
 * design — refine is point edits over the parent image, not a copy of its
 * settings. The TECHNICAL group (model family, effort, extended thinking,
 * output style, Hi-Res) is the exception: it is SHARED between modes, so the
 * baseline inherits it wholesale from the parent render's settings — a fresh
 * Refine form shows the same technical choices as Explore. The shared tech
 * slot (the user's own edits) overlays on top after every load. `seed` and
 * `is_derivative` carry over for the same reason (technical, not intent).
 *
 * Auto-refs default to "no refs" for style/material/color: refine is point
 * edits, not bulk reuse of the parent's references — the user opts in
 * explicitly via the Auto toggle. Mirrors Apple `RenderManager.refineBaseline`
 * and Android `RenderFormManager.refineBaseline()`.
 */
export function getRefineBaseline(
  defaults: Partial<RenderFormData> | null
): RenderFormData {
  return {
    ...initialFormData,
    render_model_family:
      defaults?.render_model_family ?? initialFormData.render_model_family,
    render_effort: defaults?.render_effort ?? initialFormData.render_effort,
    is_extended_thinking:
      defaults?.is_extended_thinking ?? initialFormData.is_extended_thinking,
    output_style_id:
      defaults?.output_style_id ?? initialFormData.output_style_id,
    // The custom file-based render style rides the shared tech group too (see
    // SHARED_TECH_SETTINGS_KEYS) — carry it over so a render generated from a
    // file reference restores in Refine instead of falling back to "Photo".
    output_project_reference_ids:
      defaults?.output_project_reference_ids ??
      initialFormData.output_project_reference_ids,
    is_hires: defaults?.is_hires ?? initialFormData.is_hires,
    seed: defaults?.seed ?? initialFormData.seed,
    is_derivative: defaults?.is_derivative ?? initialFormData.is_derivative,
    use_auto_style_refs: false,
    use_auto_material_refs: false,
    use_auto_color_refs: false,
  };
}
